import { Link } from "@tanstack/react-router";
import { ArrowUpDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge, FeedbackState } from "@h2j-data-team/ui";

import type { DashboardPendingChangeSample } from "@repo/shared";
import { PendingChangeRow } from "./pending-change-row";

type PendingChangesPanelProps = {
  total: number;
  conflicts: number;
  items: DashboardPendingChangeSample[];
};

export function PendingChangesPanel({ total, conflicts, items }: PendingChangesPanelProps) {
  const { t } = useTranslation("dashboard");

  const rendered = items.slice(0, 4);

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between border-b border-hairline pb-3">
        <div className="flex items-center gap-2">
          <ArrowUpDown className="h-3.5 w-3.5 text-dim" strokeWidth={1.8} />
          <h2 className="text-[14px] font-semibold text-ink">
            {t("pendingPanel.title", { defaultValue: "Pending changes" })}
          </h2>
          <Badge>{total}</Badge>
          {conflicts > 0 ? (
            <Badge variant="danger">
              {t("pendingPanel.conflictsBadge", {
                count: conflicts,
                defaultValue: `${conflicts} conflicts`,
              })}
            </Badge>
          ) : null}
        </div>
        <Link
          to="/single-app"
          search={{ q: undefined }}
          className="text-[12px] font-medium text-brand-text"
        >
          {t("pendingPanel.review", { defaultValue: "Review →" })}
        </Link>
      </div>

      {rendered.length === 0 ? (
        <FeedbackState
          state="empty"
          title={t("pendingPanel.empty", { defaultValue: "No pending changes." })}
        />
      ) : (
        <div className="flex flex-col">
          {rendered.map((item) => {
            const kind = item.conflictCount > 0 ? "conflict" : "update";
            const typeLabel =
              item.variablePending > 0 && item.masterItemPending === 0
                ? t("pendingPanel.types.variable", { defaultValue: "Variable" })
                : item.masterItemPending > 0 && item.variablePending === 0
                  ? t("pendingPanel.types.masterItem", { defaultValue: "Master item" })
                  : t("pendingPanel.types.mixed", { defaultValue: "Mixed" });
            const right = t("pendingPanel.changeCount", {
              count: item.variablePending + item.masterItemPending,
              defaultValue: `${item.variablePending + item.masterItemPending} changes`,
            });
            return (
              <PendingChangeRow
                key={item.draftId}
                kind={kind}
                typeLabel={typeLabel}
                name={item.appName ?? item.appId}
                rightLabel={right}
                connectionId={item.connectionId}
                appId={item.appId}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
